package org.example.project.ui

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.HelpOutline
import androidx.compose.material.icons.filled.Flag
import androidx.compose.material.icons.filled.FlashOn
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.filled.SkipNext
import androidx.compose.material.icons.filled.Star
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import kotlinproject.composeapp.generated.resources.*
import org.example.project.game.*

private val avatarDrawables: List<DrawableResource> = listOf(
    Res.drawable.avatargreen,
    Res.drawable.elephant,
    Res.drawable.giraffe,
    Res.drawable.koala,
    Res.drawable.leo,
    Res.drawable.loki
)
private val avatarNames: List<String> = listOf(
    "avatargreen", "elephant", "giraffe", "koala", "leo", "loki"
)

/** Avatarname (App-Format) → Drawable; Fallback avatargreen. */
private fun drawableForAvatar(name: String): DrawableResource {
    val idx = avatarNames.indexOf(name)
    return if (idx >= 0) avatarDrawables[idx] else avatarDrawables[0]
}

@Composable
fun GameScreen(
    playerName: String,
    playerAvatarIndex: Int,
    playerAvatarName: String,
    langCode: String,
    difficulty: String,
    isMultiplayer: Boolean,
    roomCode: String = "",
    isHost: Boolean = false,
    onBack: () -> Unit
) {
    if (isMultiplayer) {
        MultiplayerGameContent(
            playerName = playerName,
            playerAvatarName = playerAvatarName,
            langCode = langCode,
            difficulty = difficulty,
            initialRoomCode = roomCode,
            isHost = isHost,
            onBack = onBack
        )
    } else {
        SinglePlayerGameContent(
            playerName = playerName,
            playerAvatarIndex = playerAvatarIndex,
            langCode = langCode,
            difficulty = difficulty,
            onBack = onBack
        )
    }
}

// =====================================================================
// SINGLE PLAYER — dein Original-Code, unveraendert
// =====================================================================

@Composable
private fun SinglePlayerGameContent(
    playerName: String,
    playerAvatarIndex: Int,
    langCode: String,
    difficulty: String,
    onBack: () -> Unit
) {
    val vm = remember(langCode, difficulty) { SinglePlayerViewModel(langCode, difficulty) }
    DisposableEffect(vm) { onDispose { vm.dispose() } }

    val state by vm.state.collectAsState()
    val isLoading by vm.isLoading.collectAsState()
    val isSurrendered by vm.isSurrendered.collectAsState()
    val isFinalHinted by vm.isFinalHinted.collectAsState()

    val gold = Color(0xFFFFD700)
    val deepBlue = Color(0xFF113E99)

    Box(modifier = Modifier.fillMaxSize().background(deepBlue)) {
        if (isLoading) CircularProgressIndicator(modifier = Modifier.align(Alignment.Center), color = gold)

        state?.let { gameState ->
            val topText = when {
                gameState.isGameWon -> "GEWONNEN!"
                isSurrendered -> "AUFGEGEBEN"
                else -> "VIEL ERFOLG!"
            }
            val topColor = when {
                gameState.isGameWon -> gold
                isSurrendered -> Color.LightGray
                else -> Color.White
            }
            Text(topText, color = topColor, fontWeight = FontWeight.Bold, modifier = Modifier.align(Alignment.TopCenter).padding(top = 16.dp))

            Column(modifier = Modifier.align(Alignment.CenterStart).padding(start = 24.dp), horizontalAlignment = Alignment.CenterHorizontally) {
                Text(playerName, color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.Bold)
                Box(modifier = Modifier.size(60.dp).clip(CircleShape).background(Color.Black).border(2.dp, Color.Green, CircleShape), contentAlignment = Alignment.Center) {
                    Image(painter = painterResource(avatarDrawables[playerAvatarIndex % avatarDrawables.size]), contentDescription = null, modifier = Modifier.size(45.dp).clip(CircleShape))
                }
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.Star, null, tint = gold, modifier = Modifier.size(16.dp))
                    Text(gameState.currentScore.toString(), color = gold, fontSize = 16.sp, fontWeight = FontWeight.Bold)
                }
            }

            Column(modifier = Modifier.align(Alignment.Center).fillMaxHeight(), verticalArrangement = Arrangement.Center, horizontalAlignment = Alignment.CenterHorizontally) {
                Row(verticalAlignment = Alignment.Bottom) {
                    Column(horizontalAlignment = Alignment.End) { (1..4).forEach { i -> SpHintPill("A$i", gameState, vm, "A", isSurrendered, Modifier.padding(bottom = 6.dp, end = ((5-i)*35).dp)) } }
                    Spacer(Modifier.width(16.dp))
                    Column(horizontalAlignment = Alignment.Start) { (1..4).forEach { i -> SpHintPill("B$i", gameState, vm, "B", isSurrendered, Modifier.padding(bottom = 6.dp, start = ((5-i)*35).dp)) } }
                }

                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpThunder(gameState.columns[0], vm, isSurrendered)
                    Spacer(Modifier.width(8.dp))
                    SpColumnField(gameState.columns[0], vm, isSurrendered)
                    Spacer(Modifier.width(16.dp))
                    SpColumnField(gameState.columns[1], vm, isSurrendered)
                    Spacer(Modifier.width(8.dp))
                    SpThunder(gameState.columns[1], vm, isSurrendered)
                }

                Spacer(Modifier.height(12.dp))
                SpFinalField(gameState, vm, isSurrendered, isFinalHinted)
                Spacer(Modifier.height(12.dp))

                Row(verticalAlignment = Alignment.CenterVertically) {
                    SpThunder(gameState.columns[2], vm, isSurrendered)
                    Spacer(Modifier.width(8.dp))
                    SpColumnField(gameState.columns[2], vm, isSurrendered)
                    Spacer(Modifier.width(16.dp))
                    SpColumnField(gameState.columns[3], vm, isSurrendered)
                    Spacer(Modifier.width(8.dp))
                    SpThunder(gameState.columns[3], vm, isSurrendered)
                }

                Row(verticalAlignment = Alignment.Top) {
                    Column(horizontalAlignment = Alignment.End) { (1..4).forEach { i -> SpHintPill("C$i", gameState, vm, "C", isSurrendered, Modifier.padding(top = 6.dp, end = ((i-1)*35).dp)) } }
                    Spacer(Modifier.width(16.dp))
                    Column(horizontalAlignment = Alignment.Start) { (1..4).forEach { i -> SpHintPill("D$i", gameState, vm, "D", isSurrendered, Modifier.padding(top = 6.dp, start = ((i-1)*35).dp)) } }
                }
            }

            Row(modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 24.dp), verticalAlignment = Alignment.CenterVertically) {
                IconButton(onClick = { onBack() }) { Icon(Icons.AutoMirrored.Filled.HelpOutline, null, tint = gold) }
                Spacer(Modifier.width(20.dp))
                IconButton(onClick = { vm.surrender() }, enabled = !gameState.isGameWon && !isSurrendered) {
                    Icon(Icons.Default.Flag, "Aufgeben", tint = if (gameState.isGameWon || isSurrendered) Color.Gray.copy(0.5f) else Color.White, modifier = Modifier.size(32.dp))
                }
                val solvedCount = gameState.columns.count { it.isSolved }
                if (solvedCount >= 3) {
                    Spacer(Modifier.width(20.dp))
                    IconButton(onClick = { vm.useFinalHint() }, enabled = !gameState.isGameWon && !isSurrendered && !isFinalHinted) {
                        Icon(Icons.Default.Flag, "Finaler Tipp", tint = if (isFinalHinted || gameState.isGameWon || isSurrendered) Color.Gray else Color.Green, modifier = Modifier.size(32.dp))
                    }
                } else {
                    Spacer(Modifier.width(68.dp))
                }
                Spacer(Modifier.width(20.dp))
                IconButton(onClick = { vm.loadNewLevel() }) { Icon(Icons.Default.Refresh, null, tint = gold) }
            }
        }
    }
}

@Composable
private fun SpThunder(col: GameColumn, vm: SinglePlayerViewModel, isSurrendered: Boolean) {
    Icon(Icons.Default.FlashOn, null,
        tint = if (col.isHinted || col.isSolved || isSurrendered) Color.Gray else Color(0xFFFFD700),
        modifier = Modifier.size(28.dp).clickable(enabled = !col.isHinted && !col.isSolved && !isSurrendered) { vm.useThunder(col.id) })
}

@Composable
private fun SpColumnField(column: GameColumn, vm: SinglePlayerViewModel, isSurrendered: Boolean) {
    var input by remember(column.id, column.isSolved) { mutableStateOf("") }
    val bgColor = when {
        isSurrendered -> Color.Gray
        column.isSolved -> Color(0xFF4CAF50)
        else -> Color.White
    }
    BasicTextField(
        value = if (column.isSolved) column.columnSolution.uppercase() else input.uppercase(),
        onValueChange = { newValue ->
            if (newValue.length <= column.columnSolution.length) {
                input = newValue
                if (newValue.length == column.columnSolution.length) {
                    if (!vm.guessColumn(column.id, newValue)) input = ""
                }
            }
        },
        enabled = !column.isSolved && !isSurrendered,
        textStyle = TextStyle(color = if (column.isSolved || isSurrendered) Color.White else Color.Black, textAlign = TextAlign.Center, fontWeight = FontWeight.Bold, letterSpacing = 2.sp),
        decorationBox = { inner ->
            Box(modifier = Modifier.width(140.dp).height(35.dp).background(bgColor, RoundedCornerShape(18.dp)), contentAlignment = Alignment.Center) {
                if (input.isEmpty() && !column.isSolved && column.isHinted && !isSurrendered) {
                    Text(text = column.columnSolution.first().uppercase() + "*".repeat(column.columnSolution.length - 1), color = Color.Gray.copy(0.6f), fontWeight = FontWeight.Bold, letterSpacing = 2.sp)
                }
                inner()
            }
        }
    )
}

@Composable
private fun SpHintPill(fieldId: String, state: GameState, vm: SinglePlayerViewModel, columnId: String, isSurrendered: Boolean, modifier: Modifier) {
    val column = state.columns.find { it.id == columnId } ?: return
    val field = column.fields.find { it.id == fieldId } ?: return
    Box(
        modifier = modifier.width(150.dp).height(30.dp)
            .background(if (field.isRevealed) Color(0xFF1565C0) else Color(0xFF1565C0).copy(0.6f), RoundedCornerShape(15.dp))
            .border(1.dp, Color.White.copy(0.3f), RoundedCornerShape(15.dp))
            .clickable(!field.isRevealed && !column.isSolved && !isSurrendered) { vm.revealHint(columnId, fieldId) },
        contentAlignment = Alignment.Center
    ) {
        Text(if (field.isRevealed) field.hintText.uppercase() else fieldId, color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.Bold)
    }
}

@Composable
private fun SpFinalField(state: GameState, vm: SinglePlayerViewModel, isSurrendered: Boolean, isFinalHinted: Boolean) {
    var input by remember(state.finalSolution, state.isGameWon) { mutableStateOf("") }
    val isFinished = state.isGameWon || isSurrendered
    val bgColor = when {
        state.isGameWon -> Color(0xFF4CAF50)
        isSurrendered -> Color.Gray
        else -> Color(0xFFBBDEFB)
    }
    BasicTextField(
        value = if (isFinished) state.finalSolution.uppercase() else input.uppercase(),
        onValueChange = { newValue ->
            if (newValue.length <= state.finalSolution.length) {
                input = newValue
                if (newValue.length == state.finalSolution.length) {
                    if (!vm.guessFinal(newValue)) input = ""
                }
            }
        },
        enabled = !isFinished,
        textStyle = TextStyle(color = if (isFinished) Color.White else Color.Black, textAlign = TextAlign.Center, fontWeight = FontWeight.ExtraBold, fontSize = 18.sp, letterSpacing = 2.sp),
        decorationBox = { inner ->
            Box(modifier = Modifier.width(280.dp).height(45.dp).background(bgColor, RoundedCornerShape(22.dp)), contentAlignment = Alignment.Center) {
                if (input.isEmpty() && !isFinished) {
                    if (isFinalHinted) {
                        Text(text = state.finalSolution.first().uppercase() + "*".repeat(state.finalSolution.length - 1), color = Color.Gray.copy(0.8f), fontWeight = FontWeight.Bold, letterSpacing = 4.sp)
                    } else {
                        Text("LÖSUNG", color = Color.Gray.copy(0.5f), fontSize = 14.sp)
                    }
                }
                inner()
            }
        }
    )
}

// =====================================================================
// MULTIPLAYER — volle App-Paritaet
// =====================================================================

@Composable
private fun MultiplayerGameContent(
    playerName: String,
    playerAvatarName: String,
    langCode: String,
    difficulty: String,
    initialRoomCode: String,
    isHost: Boolean,
    onBack: () -> Unit
) {
    // Beim Host: erst Raum erstellen, dann VM mit dem neuen Code starten
    var resolvedRoomCode by remember { mutableStateOf(initialRoomCode) }
    var hostError by remember { mutableStateOf<String?>(null) }

    // Wenn Host und noch kein Code: Lobby-VM einmal benutzen um Raum zu erstellen
    if (isHost && resolvedRoomCode.isEmpty()) {
        val lobbyVm = remember {
            MultiplayerLobbyViewModel(
                playerName = playerName,
                playerAvatar = playerAvatarName,
                langCode = langCode,
                difficulty = difficulty
            )
        }
        val lobbyError by lobbyVm.errorMessage.collectAsState()
        val lobbyCode by lobbyVm.currentRoomCode.collectAsState()
        val lobbyLoading by lobbyVm.isLoading.collectAsState()

        LaunchedEffect(Unit) { lobbyVm.createRoom() }
        LaunchedEffect(lobbyCode) {
            lobbyCode?.let {
                lobbyVm.onGameStarted()
                resolvedRoomCode = it
            }
        }
        LaunchedEffect(lobbyError) { hostError = lobbyError }
        DisposableEffect(Unit) { onDispose { /* lobbyVm bleibt - wird vom GameVM weitergenutzt nicht noetig */ } }

        val gold = Color(0xFFFFD700)
        val deepBlue = Color(0xFF113E99)
        Box(modifier = Modifier.fillMaxSize().background(deepBlue)) {
            if (lobbyLoading || resolvedRoomCode.isEmpty()) {
                CircularProgressIndicator(modifier = Modifier.align(Alignment.Center), color = gold)
            }
            hostError?.let { msg ->
                Text(msg, color = Color.Red, fontWeight = FontWeight.Bold,
                    modifier = Modifier.align(Alignment.Center).padding(16.dp))
            }
        }
        return
    }

    // Eigentlicher Game-VM
    val vm = remember(resolvedRoomCode) {
        MultiplayerGameViewModel(
            initialRoomCode = resolvedRoomCode,
            isHost = isHost,
            playerName = playerName,
            playerAvatar = playerAvatarName,
            langCode = langCode,
            difficulty = difficulty
        )
    }
    DisposableEffect(vm) { onDispose { vm.dispose() } }

    val state by vm.state.collectAsState()
    val isLoading by vm.isLoading.collectAsState()
    val errorMessage by vm.errorMessage.collectAsState()
    val isMyTurn by vm.isMyTurn.collectAsState()
    val isSpectator by vm.isSpectator.collectAsState()
    val phase by vm.phase.collectAsState()
    val players by vm.players.collectAsState()
    val playerInfoMap by vm.playerInfoMap.collectAsState()
    val scoresMap by vm.scoresMap.collectAsState()
    val winner by vm.winner.collectAsState()
    val giveUpVotes by vm.giveUpVotes.collectAsState()
    val isGameFinished by vm.isGameFinished.collectAsState()
    val finalWrongCount by vm.finalWrongCount.collectAsState()
    val isFinalHinted by vm.isFinalHinted.collectAsState()
    val isAllRevealed by vm.isAllRevealed.collectAsState()
    val hintUsed by vm.hintUsed.collectAsState()
    val jokerUsed by vm.jokerUsed.collectAsState()
    val answerUsed by vm.answerUsed.collectAsState()
    val currentRoomCode by vm.roomCode.collectAsState()

    val myId = vm.playerId
    val gold = Color(0xFFFFD700)
    val deepBlue = Color(0xFF113E99)

    val isWaiting = phase == "waiting_for_guest"
    val canPlay = isMyTurn && !isGameFinished && !isSpectator && !isWaiting
    val iVoted = giveUpVotes.contains(myId)
    val otherVoted = giveUpVotes.isNotEmpty() && !iVoted

    Box(modifier = Modifier.fillMaxSize().background(deepBlue)) {
        if (isLoading) CircularProgressIndicator(modifier = Modifier.align(Alignment.Center), color = gold)

        errorMessage?.let { msg ->
            Text(msg, color = Color.Red, fontWeight = FontWeight.Bold,
                modifier = Modifier.align(Alignment.TopCenter).padding(top = 100.dp))
        }

        // === Top-Bar: Status + Raum-Code ===
        Column(
            modifier = Modifier.align(Alignment.TopCenter).padding(top = 12.dp).fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            val (topText, topColor) = when {
                isSpectator -> "ZUSCHAUER" to Color.Cyan
                winner.isNotEmpty() && winner == myId -> "GEWONNEN!" to gold
                winner.isNotEmpty() -> "${playerInfoMap[winner]?.name ?: "?"} GEWINNT" to Color.LightGray
                isGameFinished -> "SPIEL BEENDET" to Color.LightGray
                otherVoted -> "Mitspieler will aufgeben" to Color.Yellow
                isWaiting -> "WARTE AUF MITSPIELER..." to Color.White
                isMyTurn -> "DU BIST DRAN" to Color.Green
                else -> "WARTEN..." to Color.Red
            }
            Text(topText, color = topColor, fontWeight = FontWeight.Bold, fontSize = 14.sp)
            Spacer(Modifier.height(4.dp))
            Text("RAUM-CODE", color = Color.White.copy(0.7f), fontSize = 10.sp,
                fontWeight = FontWeight.Bold, letterSpacing = 2.sp)
            Text(
                text = currentRoomCode.ifEmpty { "..." },
                color = gold, fontSize = 28.sp, fontWeight = FontWeight.Black,
                letterSpacing = 6.sp
            )
        }

        // === Eigener Avatar (links) ===
        Column(
            modifier = Modifier.align(Alignment.CenterStart).padding(start = 24.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(playerName, color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.Bold)
            Box(
                modifier = Modifier.size(60.dp).clip(CircleShape).background(Color.Black)
                    .border(2.dp, if (isMyTurn && !isWaiting) Color.Yellow else Color.Green, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Image(painter = painterResource(drawableForAvatar(playerAvatarName)),
                    contentDescription = null, modifier = Modifier.size(45.dp).clip(CircleShape))
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Default.Star, null, tint = gold, modifier = Modifier.size(16.dp))
                Text((scoresMap[myId] ?: 0).toString(), color = gold, fontSize = 16.sp, fontWeight = FontWeight.Bold)
            }
        }

        // === Gegner-Avatare (rechts, vertikal gestapelt) ===
        val opponents = players.filter { it != myId }.take(3)
        if (opponents.isNotEmpty()) {
            Column(
                modifier = Modifier.align(Alignment.CenterEnd).padding(end = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                opponents.forEach { oppId ->
                    val info = playerInfoMap[oppId]
                    val oppName = info?.name ?: "?"
                    val oppAvatar = info?.avatar ?: "avatargreen"
                    val oppScore = scoresMap[oppId] ?: 0
                    val oppHasTurn = (phase == "playing" || phase == "finished") &&
                            opponents.indexOf(oppId) >= 0 && !isMyTurn && !isWaiting &&
                            !isGameFinished
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Text(oppName, color = Color.White, fontSize = 10.sp, fontWeight = FontWeight.Bold)
                        Box(
                            modifier = Modifier.size(45.dp).clip(CircleShape).background(Color.Black)
                                .border(2.dp, if (oppHasTurn) Color.Yellow else Color.Gray, CircleShape),
                            contentAlignment = Alignment.Center
                        ) {
                            Image(painter = painterResource(drawableForAvatar(oppAvatar)),
                                contentDescription = null, modifier = Modifier.size(32.dp).clip(CircleShape))
                        }
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(Icons.Default.Star, null, tint = gold, modifier = Modifier.size(12.dp))
                            Text(oppScore.toString(), color = gold, fontSize = 12.sp, fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        }

        // === Spielfeld ===
        state?.let { gameState ->
            Column(
                modifier = Modifier.align(Alignment.Center).fillMaxHeight(),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Row(verticalAlignment = Alignment.Bottom) {
                    Column(horizontalAlignment = Alignment.End) {
                        (1..4).forEach { i -> MpHintPill("A$i", gameState, vm, "A", canPlay, hintUsed,
                            Modifier.padding(bottom = 6.dp, end = ((5-i)*35).dp)) }
                    }
                    Spacer(Modifier.width(16.dp))
                    Column(horizontalAlignment = Alignment.Start) {
                        (1..4).forEach { i -> MpHintPill("B$i", gameState, vm, "B", canPlay, hintUsed,
                            Modifier.padding(bottom = 6.dp, start = ((5-i)*35).dp)) }
                    }
                }

                Row(verticalAlignment = Alignment.CenterVertically) {
                    MpThunder(gameState.columns[0], vm, canPlay, jokerUsed)
                    Spacer(Modifier.width(8.dp))
                    MpColumnField(gameState.columns[0], vm, canPlay, answerUsed)
                    Spacer(Modifier.width(16.dp))
                    MpColumnField(gameState.columns[1], vm, canPlay, answerUsed)
                    Spacer(Modifier.width(8.dp))
                    MpThunder(gameState.columns[1], vm, canPlay, jokerUsed)
                }

                Spacer(Modifier.height(12.dp))
                MpFinalField(gameState, vm, isGameFinished, isFinalHinted, isAllRevealed, canPlay)
                Spacer(Modifier.height(12.dp))

                Row(verticalAlignment = Alignment.CenterVertically) {
                    MpThunder(gameState.columns[2], vm, canPlay, jokerUsed)
                    Spacer(Modifier.width(8.dp))
                    MpColumnField(gameState.columns[2], vm, canPlay, answerUsed)
                    Spacer(Modifier.width(16.dp))
                    MpColumnField(gameState.columns[3], vm, canPlay, answerUsed)
                    Spacer(Modifier.width(8.dp))
                    MpThunder(gameState.columns[3], vm, canPlay, jokerUsed)
                }

                Row(verticalAlignment = Alignment.Top) {
                    Column(horizontalAlignment = Alignment.End) {
                        (1..4).forEach { i -> MpHintPill("C$i", gameState, vm, "C", canPlay, hintUsed,
                            Modifier.padding(top = 6.dp, end = ((i-1)*35).dp)) }
                    }
                    Spacer(Modifier.width(16.dp))
                    Column(horizontalAlignment = Alignment.Start) {
                        (1..4).forEach { i -> MpHintPill("D$i", gameState, vm, "D", canPlay, hintUsed,
                            Modifier.padding(top = 6.dp, start = ((i-1)*35).dp)) }
                    }
                }
            }

            // === Untere Buttons ===
            Row(
                modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = 24.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                IconButton(onClick = { onBack() }) {
                    Icon(Icons.AutoMirrored.Filled.HelpOutline, null, tint = gold)
                }
                Spacer(Modifier.width(16.dp))

                // Aufgeben (Konsens)
                IconButton(
                    onClick = { vm.voteGiveUp() },
                    enabled = !isGameFinished && !iVoted && !isSpectator
                ) {
                    Icon(Icons.Default.Flag, "Aufgeben",
                        tint = if (isGameFinished || iVoted || isSpectator) Color.Gray.copy(0.5f) else Color.White,
                        modifier = Modifier.size(32.dp))
                }
                Spacer(Modifier.width(16.dp))

                // Skip
                IconButton(
                    onClick = { vm.skipTurn() },
                    enabled = canPlay
                ) {
                    Icon(Icons.Default.SkipNext, "Skip",
                        tint = if (canPlay) Color.White else Color.Gray.copy(0.5f),
                        modifier = Modifier.size(32.dp))
                }
                Spacer(Modifier.width(16.dp))

                // Final-Tipp: erst sichtbar nach 2 Fehlversuchen UND alle 4 Spalten geloest
                val allColsSolved = gameState.columns.all { it.isSolved }
                if (finalWrongCount >= 2 && allColsSolved) {
                    IconButton(
                        onClick = { vm.useFinalHint() },
                        enabled = !isGameFinished && !isFinalHinted && canPlay
                    ) {
                        Icon(Icons.Default.Flag, "Finaler Tipp",
                            tint = if (isFinalHinted || isGameFinished) Color.Gray else Color.Green,
                            modifier = Modifier.size(32.dp))
                    }
                    Spacer(Modifier.width(16.dp))
                }

                // Refresh — nur Host
                if (isHost) {
                    IconButton(onClick = { vm.loadNewLevel() }) {
                        Icon(Icons.Default.Refresh, null, tint = gold)
                    }
                }
            }
        }
    }
}

// === MP Hilfs-Composables ===

@Composable
private fun MpThunder(col: GameColumn, vm: MultiplayerGameViewModel, canPlay: Boolean, jokerUsed: Boolean) {
    val enabled = canPlay && !jokerUsed && !col.isHinted && !col.isSolved
    Icon(
        Icons.Default.FlashOn, null,
        tint = if (!enabled) Color.Gray else Color(0xFFFFD700),
        modifier = Modifier.size(28.dp)
            .clickable(enabled = enabled) { vm.useThunder(col.id) }
    )
}

@Composable
private fun MpColumnField(column: GameColumn, vm: MultiplayerGameViewModel, canPlay: Boolean, answerUsed: Boolean) {
    var input by remember(column.id, column.isSolved) { mutableStateOf("") }
    val isLocked = !canPlay || answerUsed || column.isSolved
    val bgColor = when {
        column.isSolved -> Color(0xFF4CAF50)
        !canPlay || answerUsed -> Color.LightGray
        else -> Color.White
    }
    BasicTextField(
        value = if (column.isSolved) column.columnSolution.uppercase() else input.uppercase(),
        onValueChange = { newValue ->
            if (newValue.length <= column.columnSolution.length) {
                input = newValue
                if (newValue.length == column.columnSolution.length) {
                    if (!vm.guessColumn(column.id, newValue)) input = ""
                }
            }
        },
        enabled = !isLocked,
        textStyle = TextStyle(color = if (column.isSolved) Color.White else Color.Black, textAlign = TextAlign.Center, fontWeight = FontWeight.Bold, letterSpacing = 2.sp),
        decorationBox = { inner ->
            Box(modifier = Modifier.width(140.dp).height(35.dp).background(bgColor, RoundedCornerShape(18.dp)), contentAlignment = Alignment.Center) {
                if (input.isEmpty() && !column.isSolved && column.isHinted) {
                    Text(text = column.columnSolution.first().uppercase() + "*".repeat(column.columnSolution.length - 1), color = Color.Gray.copy(0.6f), fontWeight = FontWeight.Bold, letterSpacing = 2.sp)
                }
                inner()
            }
        }
    )
}

@Composable
private fun MpHintPill(
    fieldId: String,
    state: GameState,
    vm: MultiplayerGameViewModel,
    columnId: String,
    canPlay: Boolean,
    hintUsed: Boolean,
    modifier: Modifier
) {
    val column = state.columns.find { it.id == columnId } ?: return
    val field = column.fields.find { it.id == fieldId } ?: return
    val canClick = canPlay && !hintUsed && !field.isRevealed && !column.isSolved
    Box(
        modifier = modifier.width(150.dp).height(30.dp)
            .background(if (field.isRevealed) Color(0xFF1565C0) else Color(0xFF1565C0).copy(0.6f), RoundedCornerShape(15.dp))
            .border(1.dp, Color.White.copy(0.3f), RoundedCornerShape(15.dp))
            .clickable(enabled = canClick) { vm.revealHint(columnId, fieldId) },
        contentAlignment = Alignment.Center
    ) {
        Text(if (field.isRevealed) field.hintText.uppercase() else fieldId,
            color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.Bold)
    }
}

@Composable
private fun MpFinalField(
    state: GameState,
    vm: MultiplayerGameViewModel,
    isGameFinished: Boolean,
    isFinalHinted: Boolean,
    isAllRevealed: Boolean,
    canPlay: Boolean
) {
    var input by remember(state.finalSolution, state.isGameWon) { mutableStateOf("") }
    val isFinished = state.isGameWon || isGameFinished
    val isLocked = isFinished || !canPlay
    val bgColor = when {
        state.isGameWon -> Color(0xFF4CAF50)
        isGameFinished -> Color.Gray
        !canPlay -> Color(0xFFE0E0E0)
        else -> Color(0xFFBBDEFB)
    }
    BasicTextField(
        value = if (isFinished) state.finalSolution.uppercase() else input.uppercase(),
        onValueChange = { newValue ->
            if (newValue.length <= state.finalSolution.length) {
                input = newValue
                if (newValue.length == state.finalSolution.length) {
                    if (!vm.guessFinal(newValue)) input = ""
                }
            }
        },
        enabled = !isLocked,
        textStyle = TextStyle(color = if (isFinished) Color.White else Color.Black, textAlign = TextAlign.Center, fontWeight = FontWeight.ExtraBold, fontSize = 18.sp, letterSpacing = 2.sp),
        decorationBox = { inner ->
            Box(modifier = Modifier.width(280.dp).height(45.dp).background(bgColor, RoundedCornerShape(22.dp)), contentAlignment = Alignment.Center) {
                if (input.isEmpty() && !isFinished) {
                    when {
                        isFinalHinted -> {
                            // X _ _ _ Y (App-Format)
                            val w = state.finalSolution
                            if (w.length >= 2) {
                                Text("${w.first().uppercase()} _ _ _ ${w.last().uppercase()}",
                                    color = Color.Gray.copy(0.8f), fontWeight = FontWeight.Bold, letterSpacing = 4.sp)
                            }
                        }
                        isAllRevealed -> {
                            // X... (nur erster Buchstabe)
                            Text("${state.finalSolution.first().uppercase()}...",
                                color = Color.Gray.copy(0.8f), fontWeight = FontWeight.Bold)
                        }
                        else -> Text("Tadaa", color = Color(0xFF1565C0).copy(0.6f), fontSize = 16.sp, fontWeight = FontWeight.Bold)
                    }
                }
                inner()
            }
        }
    )
}